import { useState } from 'react';
import { Button, Group, Stack, Text } from '@mantine/core';
import { IconLanguage } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useLocaleStore } from './localeStore';
import { localeOption } from './localeOptions';
import { LanguageModal } from './LanguagePicker';

/**
 * Language switcher for the public pages (sign-in, registration).
 *
 * The button shows the current language in its own language, and opens the
 * shared LanguageModal. The "hint" line explains where the current pick came
 * from ("auto-detected from browser" vs "selected by you") so the user knows we
 * already tried.
 *
 * This replaced a row of four pills, which stopped being viable as soon as the
 * language list could grow — see LanguagePicker.
 */
export function LanguageSwitcher() {
  const { t } = useTranslation();
  const { locale, source } = useLocaleStore();
  const [opened, setOpened] = useState(false);

  const current = localeOption(locale);

  return (
    <>
      <Stack gap={6} align="flex-start">
        <Group gap={6} align="center">
          <Text size="xs" c="dimmed">
            {source === 'auto'
              ? t('languageSwitcher.autoDetected')
              : t('languageSwitcher.userChosen')}
          </Text>
          <Text size="xs" c="dimmed">
            ·
          </Text>
          <Text size="xs" c="dimmed" ff="monospace">
            {locale}
          </Text>
        </Group>
        <Button
          size="xs"
          variant="default"
          leftSection={<IconLanguage size={14} stroke={1.75} />}
          onClick={() => setOpened(true)}
        >
          {current?.native ?? locale}
        </Button>
      </Stack>

      <LanguageModal opened={opened} onClose={() => setOpened(false)} />
    </>
  );
}
